import { useMutation } from '@tanstack/react-query';
import { useCallback } from 'react';
// import { ProjectAPI } from 'api/project';
import { confirm } from 'components/atoms/modal';
import { toastService } from 'components/molecules/toast';
import { CpModal } from 'components/organisms/customer-portal/components/modal/cp-modal';
import { LdsApiClient } from 'marketplace/api/local-dev-server-api';
import { ServerMethodApi } from 'marketplace/api/marketplace-service-api';
import { RlMarketplaceApi } from 'marketplace/api/rl-marketplace-api';
import { useAccountDataQuery } from 'queries/account';
import { logger } from 'services';
import { useGetMarketplaceAuthToken } from './use-marketplace-hooks';
export const useWidgetCommunicationHandler = (appId, { isCustomerPortal, isLds } = {
    isCustomerPortal: false,
    isLds: false
}, context, onSendMessage)=>{
    var _accountData_userProfile, _accountData_userProfile1;
    const { data: accountData } = useAccountDataQuery();
    const show = useCallback((event)=>{
        var _ref;
        const { element, options } = (_ref = event === null || event === void 0 ? void 0 : event.data) !== null && _ref !== void 0 ? _ref : {};
        switch(element){
            case 'dialog':
                {
                    return new Promise((resolve)=>{
                        confirm({
                            title: options === null || options === void 0 ? void 0 : options.title,
                            content: options === null || options === void 0 ? void 0 : options.content,
                            onOk: ()=>resolve(true),
                            onCancel: ()=>resolve(false),
                            getContainer: ()=>document.getElementById('cp-modal-root') || document.body,
                            CustomModal: isCustomerPortal ? CpModal : undefined
                        });
                    });
                }
            case 'notification':
                {
                    const { message, type = 'info' } = options !== null && options !== void 0 ? options : {};
                    const toast = toastService({
                        message
                    });
                    const typeFn = toast[type];
                    if (typeFn) {
                        typeFn();
                    }
                    return Promise.resolve(true);
                }
            default:
                break;
        }
        return Promise.resolve(false);
    }, [
        isCustomerPortal
    ]);
    // SDK interface.sendMessage. Only a surface that owns a chat passes onSendMessage. Elsewhere the
    // reply is immediate, so the widget's promise never waits on a host that cannot deliver it.
    const sendMessage = useCallback(async (event)=>{
        var _event_data;
        const message = event === null || event === void 0 ? void 0 : (_event_data = event.data) === null || _event_data === void 0 ? void 0 : _event_data.message;
        if (typeof message !== 'string' || !message.trim() || message.length > 2000) {
            return {
                success: false,
                error: 'invalid_message'
            };
        }
        if (!onSendMessage) {
            return {
                success: false,
                error: 'no_chat_context'
            };
        }
        try {
            await onSendMessage(message);
            return {
                success: true
            };
        } catch (error) {
            logger.error('widget sendMessage failed', {
                error
            });
            return {
                success: false,
                error: 'send_failed'
            };
        }
    }, [
        onSendMessage
    ]);
    const registerCallbacks = useCallback((widget, data)=>{
        if (!widget) {
            return ()=>{};
        }
        const getData = async (event)=>{
            var _event_data;
            const eventName = event === null || event === void 0 ? void 0 : (_event_data = event.data) === null || _event_data === void 0 ? void 0 : _event_data.dataIdentifier;
            if (data === null || data === void 0 ? void 0 : data[eventName]) {
                return Promise.resolve({
                    response: await (data === null || data === void 0 ? void 0 : data[eventName]())
                });
            }
            if (eventName === 'user') {
                var _accountData_userProfile;
                const user = accountData === null || accountData === void 0 ? void 0 : (_accountData_userProfile = accountData.userProfile) === null || _accountData_userProfile === void 0 ? void 0 : _accountData_userProfile.user;
                let responseUser = user;
                try {
                    if (user) {
                        var _accountData_userProfile_permissions, _accountData_userProfile1;
                        responseUser = {
                            ...user,
                            permissionSet: accountData === null || accountData === void 0 ? void 0 : (_accountData_userProfile1 = accountData.userProfile) === null || _accountData_userProfile1 === void 0 ? void 0 : (_accountData_userProfile_permissions = _accountData_userProfile1.permissions) === null || _accountData_userProfile_permissions === void 0 ? void 0 : _accountData_userProfile_permissions.permissionSet
                        };
                    }
                } catch (error) {
                    logger.error('Error getting user permission set', {
                        error
                    });
                }
                return Promise.resolve({
                    response: responseUser
                });
            }
            if (eventName === 'account') {
                return Promise.resolve({
                    response: accountData === null || accountData === void 0 ? void 0 : accountData.account
                });
            }
            return Promise.resolve();
        };
        const invokeServerAction = (event)=>{
            var _event_data, _event_data1, _event_data2, _event_data3;
            // Guest Communicator acks prior replies with { continue } and no action.
            if ((event === null || event === void 0 ? void 0 : event.data) && typeof event.data === 'object' && 'continue' in event.data && !event.data.action) {
                return Promise.resolve({
                    success: true,
                    data: {
                        continue: true
                    }
                });
            }
            if (!(event === null || event === void 0 ? void 0 : (_event_data = event.data) === null || _event_data === void 0 ? void 0 : _event_data.action)) {
                return Promise.reject(new Error('Missing server action name'));
            }
            if (isLds) {
                var _event_data4, _accountData_userProfile, _accountData_userProfile_user, _accountData_userProfile1, _accountData_userProfile_user1, _accountData_userProfile2, _accountData_account, _accountData_account1, _event_data5, _event_data6;
                return LdsApiClient.invokeServerMethod(event === null || event === void 0 ? void 0 : (_event_data4 = event.data) === null || _event_data4 === void 0 ? void 0 : _event_data4.action, {
                    user: (accountData === null || accountData === void 0 ? void 0 : (_accountData_userProfile = accountData.userProfile) === null || _accountData_userProfile === void 0 ? void 0 : _accountData_userProfile.user) ? {
                        userId: accountData === null || accountData === void 0 ? void 0 : (_accountData_userProfile1 = accountData.userProfile) === null || _accountData_userProfile1 === void 0 ? void 0 : (_accountData_userProfile_user = _accountData_userProfile1.user) === null || _accountData_userProfile_user === void 0 ? void 0 : _accountData_userProfile_user.userId,
                        emailId: accountData === null || accountData === void 0 ? void 0 : (_accountData_userProfile2 = accountData.userProfile) === null || _accountData_userProfile2 === void 0 ? void 0 : (_accountData_userProfile_user1 = _accountData_userProfile2.user) === null || _accountData_userProfile_user1 === void 0 ? void 0 : _accountData_userProfile_user1.emailId
                    } : undefined,
                    account: (accountData === null || accountData === void 0 ? void 0 : accountData.account) ? {
                        accountId: accountData === null || accountData === void 0 ? void 0 : (_accountData_account = accountData.account) === null || _accountData_account === void 0 ? void 0 : _accountData_account.accountId,
                        accountName: accountData === null || accountData === void 0 ? void 0 : (_accountData_account1 = accountData.account) === null || _accountData_account1 === void 0 ? void 0 : _accountData_account1.accountName
                    } : undefined,
                    ...context !== null && context !== void 0 ? context : {}
                }, event === null || event === void 0 ? void 0 : (_event_data5 = event.data) === null || _event_data5 === void 0 ? void 0 : _event_data5.data, event === null || event === void 0 ? void 0 : (_event_data6 = event.data) === null || _event_data6 === void 0 ? void 0 : _event_data6.options);
            }
            return RlMarketplaceApi.runServerAction(appId, event === null || event === void 0 ? void 0 : (_event_data1 = event.data) === null || _event_data1 === void 0 ? void 0 : _event_data1.action, event === null || event === void 0 ? void 0 : (_event_data2 = event.data) === null || _event_data2 === void 0 ? void 0 : _event_data2.data, event === null || event === void 0 ? void 0 : (_event_data3 = event.data) === null || _event_data3 === void 0 ? void 0 : _event_data3.options);
        };
        const getInvokeServerActionResponse = (event)=>{
            var _event_data;
            const requestId = event === null || event === void 0 ? void 0 : (_event_data = event.data) === null || _event_data === void 0 ? void 0 : _event_data.requestId;
            if (!requestId) {
                return Promise.reject();
            }
            if (isLds) {
                return LdsApiClient.getInvokeServerActionResponse(requestId);
            }
            return RlMarketplaceApi.getInvokeServerActionResponse(appId, requestId);
        };
        widget.on('GET_DATA', getData);
        widget.on('INVOKE_SERVER_ACTION', invokeServerAction);
        widget.on('SHOW_INTERFACE', show);
        widget.on('SEND_MESSAGE', sendMessage);
        widget.on('GET_INVOKE_SERVER_ACTION_RESPONSE', getInvokeServerActionResponse);
        return ()=>{
            widget.off('GET_DATA', getData);
            widget.off('INVOKE_SERVER_ACTION', invokeServerAction);
            widget.off('SHOW_INTERFACE', show);
            widget.off('SEND_MESSAGE', sendMessage);
            widget.off('GET_INVOKE_SERVER_ACTION_RESPONSE', getInvokeServerActionResponse);
        };
    }, [
        accountData === null || accountData === void 0 ? void 0 : accountData.account,
        accountData === null || accountData === void 0 ? void 0 : (_accountData_userProfile = accountData.userProfile) === null || _accountData_userProfile === void 0 ? void 0 : _accountData_userProfile.user,
        appId,
        isLds,
        show,
        sendMessage,
        context
    ]);
    const invokeServerMethodLocal = useCallback((name, payload)=>{
        var _accountData_userProfile;
        return LdsApiClient.invokeServerMethod(name, {
            user: accountData === null || accountData === void 0 ? void 0 : (_accountData_userProfile = accountData.userProfile) === null || _accountData_userProfile === void 0 ? void 0 : _accountData_userProfile.user,
            account: accountData === null || accountData === void 0 ? void 0 : accountData.account
        }, payload);
    }, [
        accountData === null || accountData === void 0 ? void 0 : accountData.account,
        accountData === null || accountData === void 0 ? void 0 : (_accountData_userProfile1 = accountData.userProfile) === null || _accountData_userProfile1 === void 0 ? void 0 : _accountData_userProfile1.user
    ]);
    const invokeServerMethod = useCallback((name, payload)=>{
        return RlMarketplaceApi.runServerAction(appId, name, payload);
    }, [
        appId
    ]);
    return {
        registerCallbacks,
        invokeServerMethodLocal,
        invokeServerMethod,
        show
    };
};
export const useInvokeMarketplaceServerMethod = ()=>{
    useGetMarketplaceAuthToken();
    return useMutation(({ appId, name, payload })=>{
        return ServerMethodApi.invokeServerMethod(appId, name, payload);
    });
};
